/*
 * A conversation's switch for AI chats (S-19 §3.2.1 and §6, A-4 §3): whether Claude, inside an AI
 * chat, may read this conversation. The server keeps the switch and one more rule this file never
 * computes — the audience wall: Claude reads it only from an AI chat whose owners could all open the
 * conversation themselves (`internal/core/tools/reach.go`). The words on screen say that wall, so a
 * person who opens a private channel knows its words stay among its members' chats.
 *
 * Who may switch it is the matrix's `chat.agents.set` (`- - MN "MN, or AU without M" -`): a team
 * member with an active row, which includes both people of a direct conversation, and an
 * administrator outside a channel or a private channel, whose write asks for the password like every
 * other write from outside (`manage.ts`). A guest never: a guest reads the state as one line. The
 * client's answer only decides which control is drawn; the server decides at the route.
 *
 * Opening asks once, closing never does. An open channel does not ask either: everybody in its groups
 * may read it already, so their AI chats reach nobody new. A private channel and a direct conversation
 * are their members alone, so the confirm says that everyone in it will see who opened it — the server
 * writes the system row `agents_changed` for every change, and the band carries the mark for as long
 * as it is on, for whoever joins later.
 *
 * Two places draw the control: the details column's box for a channel and a private channel
 * (`AgentsBox`), and the ⋯ menu's check item for a direct or group conversation, whose details column
 * is the other person's profile (`ConversationMenu.tsx`). Both open the same confirm (`AgentsConfirm`)
 * and write through the same call.
 */

import { useId, useState } from "react"
import { formatList } from "../../../core/i18n/format.ts"
import { t as coreT } from "../../../core/i18n/index.ts"
import { isAdmin, isTeamMember, type StandingSource } from "../../../core/people/standing.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, FormAlert } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { isDirect } from "../state/selectors.ts"
import type { ConversationKind, ConversationView } from "../wire/views.ts"
import { setAgents } from "./manage.ts"

/**
 * Whether this reader may switch the conversation: `chat.agents.set`'s cells read for the client. A
 * member's way is their own row, and only a team member's; an administrator's way is from outside,
 * and only into a channel or a private channel — a direct conversation is its people's alone. Whether
 * the conversation is archived is a separate question (`AgentsBox` draws the box disabled then).
 */
export function maySwitchAgents(
	meta: ConversationView,
	isMember: boolean,
	reader: StandingSource | null | undefined,
): boolean {
	if (isMember) return isTeamMember(reader)
	return isAdmin(reader) && !isDirect(meta.kind)
}

/**
 * Whether opening it asks first: everything but an open channel, whose groups read it anyway, so
 * their AI chats reach nobody new by it.
 */
function asksBeforeOpening(kind: ConversationKind): boolean {
	return kind !== "channel"
}

/** The band mark's words: a room is a channel, a direct or group conversation a conversation. */
export function agentsMarkLabel(kind: ConversationKind): string {
	return isDirect(kind) ? t("chat.header.agents.direct") : t("chat.header.agents.channel")
}

/**
 * The confirm that stands before a conversation is opened to AI chats. A direct or group
 * conversation names the people on the other side, because they are who it tells; a private channel
 * says "everyone here". Nothing is written until *Let AI chats read* is pressed, and a refusal stays
 * in the dialog.
 */
export function AgentsConfirm({
	meta,
	open,
	onOpenChange,
}: {
	meta: ConversationView
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	const others = (meta.memberIds ?? [])
		.filter((id) => id !== meId)
		.map((id) => users[id]?.displayName ?? "")
		.filter((name) => name !== "")
	const write = useAsyncAction(async () => {
		await setAgents(meta.id, true)
		onOpenChange(false)
	})
	const names = formatList(others)
	const body =
		!isDirect(meta.kind) || others.length === 0
			? t("chat.agents.confirmBody.private")
			: others.length === 1
				? t("chat.agents.confirmBody.direct.one", { names })
				: t("chat.agents.confirmBody.direct", {
						everyone: formatList([t("chat.agents.you"), ...others]),
						names,
					})
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				write.reset()
				onOpenChange(next)
			}}
			title={t("chat.agents.confirmTitle")}
			description={body}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					<Button
						variant="primary"
						label={t("chat.agents.confirm")}
						disabled={write.busy}
						onClick={() => void write.run()}
					/>
				</div>
			}
		>
			<FormAlert messageKey={write.error} />
		</Dialog>
	)
}

/**
 * The details column's box for a channel or a private channel: *AI chats can read this channel*, with
 * the hint that says the wall and where the words may go. A reader who may not switch it — a guest, or
 * somebody who only reads a channel without being in it — reads the state as one line instead, and an
 * archived conversation shows the box disabled, under the column's own archived note.
 */
export function AgentsBox({ meta, isMember }: { meta: ConversationView; isMember: boolean }) {
	const hintId = useId()
	const reader = useModel((model) => model.me)
	const [confirming, setConfirming] = useState(false)
	const write = useAsyncAction(async (available: boolean) => {
		await setAgents(meta.id, available)
	})
	if (!maySwitchAgents(meta, isMember, reader)) {
		return (
			<p className="tb-convdetails__fact">
				{meta.agentsAvailable ? t("chat.details.agentsOn") : t("chat.details.agentsOff")}
			</p>
		)
	}
	const choose = (available: boolean) => {
		if (available && asksBeforeOpening(meta.kind)) setConfirming(true)
		else void write.run(available)
	}
	return (
		<div className="tb-field">
			<label className="tb-check">
				<input
					type="checkbox"
					checked={meta.agentsAvailable}
					disabled={write.busy || meta.archivedAt !== undefined}
					aria-describedby={hintId}
					onChange={(event) => choose(event.target.checked)}
				/>
				<span className="tb-check__label">{t("chat.details.agents")}</span>
			</label>
			<p className="tb-field__hint tb-check__hint" id={hintId}>
				{t("chat.details.agentsHint")}
			</p>
			<FormAlert messageKey={write.error} />
			<AgentsConfirm meta={meta} open={confirming} onOpenChange={setConfirming} />
		</div>
	)
}
